import React from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import { QUESTIONNAIRE_FIELD_TYPES } from "../../constants/anmalningQuestionnaireFieldTypes";
import { getMm } from "../../theme/modernMaterialEnterprise";

const FONT = "'Plus Jakarta Sans', sans-serif";

/**
 * @param {{
 *   fields: object[];
 *   answers: Record<string, unknown>;
 *   onChange: (fieldId: string, value: unknown) => void;
 *   isDarkMode?: boolean;
 *   fieldSx?: object;
 * }} props
 */
export default function PublicQuestionnaireForm({
  fields = [],
  answers,
  onChange,
  isDarkMode = false,
  fieldSx = {},
}) {
  const { t } = useTranslation();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {fields.filter((field) => !field.hidden).map((field) => {
          if (field.type === QUESTIONNAIRE_FIELD_TYPES.HEADING) {
            return (
              <Box key={field.id} sx={{ pt: 1 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 800,
                    fontSize: "1.0625rem",
                    color: getMm(isDarkMode).onSurface,
                  }}
                >
                  {field.label}
                </Typography>
              </Box>
            );
          }

          const value = answers[field.id];

          if (
            field.type === QUESTIONNAIRE_FIELD_TYPES.CONSENT ||
            field.type === QUESTIONNAIRE_FIELD_TYPES.MULTI_SELECT ||
            field.type === QUESTIONNAIRE_FIELD_TYPES.PHOTO
          ) {
            const selected = Array.isArray(value) ? value : [];
            const acceptLabel =
              field.acceptLabel ||
              (Array.isArray(field.options) && field.options[0] ? field.options[0] : field.label);

            if (field.type === QUESTIONNAIRE_FIELD_TYPES.CONSENT) {
              const bodyText = String(field.body || "").trim();
              return (
                <Box key={field.id} component="fieldset">
                  <Typography
                    component="legend"
                    sx={{
                      fontFamily: FONT,
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: getMm(isDarkMode).onSurface,
                      mb: 1,
                    }}
                  >
                    {field.label}
                    {field.required ? " *" : ""}
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                      borderRadius: "12px",
                      bgcolor: getMm(isDarkMode).surfaceLow,
                      px: { xs: 2, sm: 2.5 },
                      py: 2,
                      mb: 1.5,
                      maxHeight: { xs: 280, sm: 320 },
                      overflow: "auto",
                    }}
                  >
                    {bodyText ? (
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.7,
                          fontFamily: FONT,
                          fontSize: "0.9375rem",
                          color: getMm(isDarkMode).onSurfaceVariant,
                        }}
                      >
                        {field.body}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontStyle: "italic",
                          color: getMm(isDarkMode).onSurfaceVariant,
                          fontSize: "0.9375rem",
                        }}
                      >
                        {t("anmalningar_consent_empty_body")}
                      </Typography>
                    )}
                  </Box>
                  <FormControlLabel
                    required={field.required}
                    control={
                      <Checkbox
                        checked={selected.includes(acceptLabel)}
                        onChange={(e) => {
                          onChange(
                            field.id,
                            e.target.checked ? [acceptLabel] : []
                          );
                        }}
                      />
                    }
                    label={acceptLabel}
                    sx={{
                      fontFamily: FONT,
                      alignItems: "flex-start",
                      "& .MuiFormControlLabel-label": {
                        lineHeight: 1.5,
                        pt: 0.25,
                      },
                    }}
                  />
                </Box>
              );
            }

            return (
              <FormControl key={field.id} component="fieldset" required={field.required}>
                <FormLabel
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 600,
                    color: getMm(isDarkMode).onSurfaceVariant,
                    mb: 0.5,
                  }}
                >
                  {field.label}
                </FormLabel>
                <FormGroup>
                  {(field.options || []).map((opt) => (
                    <FormControlLabel
                      key={opt}
                      control={
                        <Checkbox
                          checked={selected.includes(opt)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...selected, opt]
                              : selected.filter((x) => x !== opt);
                            onChange(field.id, next);
                          }}
                        />
                      }
                      label={opt}
                      sx={{ fontFamily: FONT }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            );
          }

          if (field.type === QUESTIONNAIRE_FIELD_TYPES.SINGLE_SELECT) {
            return (
              <FormControl key={field.id} component="fieldset" required={field.required}>
                <FormLabel
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 600,
                    color: getMm(isDarkMode).onSurfaceVariant,
                    mb: 0.5,
                  }}
                >
                  {field.label}
                </FormLabel>
                <RadioGroup
                  value={value ?? ""}
                  onChange={(e) => onChange(field.id, e.target.value)}
                >
                  {(field.options || []).map((opt) => (
                    <FormControlLabel
                      key={opt}
                      value={opt}
                      control={<Radio />}
                      label={opt}
                      sx={{ fontFamily: FONT }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            );
          }

          if (field.type === QUESTIONNAIRE_FIELD_TYPES.DATE) {
            return (
              <DatePicker
                key={field.id}
                label={field.label}
                value={value ? new Date(value) : null}
                onChange={(d) => onChange(field.id, d ? d.toISOString().slice(0, 10) : "")}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: field.required,
                    sx: fieldSx,
                  },
                }}
              />
            );
          }

          return (
            <TextField
              key={field.id}
              fullWidth
              label={field.label}
              value={value ?? ""}
              onChange={(e) => onChange(field.id, e.target.value)}
              required={field.required}
              size="small"
              sx={fieldSx}
            />
          );
        })}

        {fields.length === 0 ? (
          <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontFamily: FONT }}>
            {t("anmalningar_q_no_fields")}
          </Typography>
        ) : null}
      </Box>
    </LocalizationProvider>
  );
}
